import { useCallback, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import { generateRandomString } from "utils/helper";
import * as services from "../services/yearAccumulateApi";
import useOptions from "features/masterData/hooks/useOptions";
import { useTranslation } from "react-i18next";
import {
  setIsLoading,
  setYearAccumulateList,
} from "../slices/yearAccumulateSlice";

function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    pagination: {
      page: pagination.page,
      size: pagination.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useYearAccumulateList() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.yearAccumulate.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const yearAccumulateList = useSelector(
    (state) => state.yearAccumulate.yearAccumulateList
  );
  const [currentYearAccumulate, setCurrentYearAccumulate] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);

  const initialValues = useMemo(() => {
    return {
      ...currentYearAccumulate,
      incomeDeductionId: currentYearAccumulate?.incomeDeduction?.id,
    };
  }, [currentYearAccumulate]);

  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);
  const { amountIncomeDeductionOptions = [], getAmountIncomeDeductionOptions } = useOptions();

  useEffect(() => {
    getAmountIncomeDeductionOptions()
  }, [])

  const onEdit = (record) => {
    setCurrentYearAccumulate(record);
    setModalOpen(true);
  };

  const onCreate = useCallback(() => {
    setCurrentYearAccumulate({});
    setModalOpen(true);
  }, []);

  const onCancel = useCallback(() => {
    setCurrentYearAccumulate({});
    setModalOpen(false);
  }, []);

  const onSearch = useCallback((values) => {
    values.page = 0;
    values.size = 50;
    getYearAccumulateList(values)
  }, []);

  // console.log("amountIncomeDeductionOptions", getAmountIncomeDeductionOptions);
  const getYearAccumulateList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getYearAccumulateList({
          requestId: generateRandomString(),
          companyId: currentCompany,
          ...params.pagination,
          sortBy: params.sortBy,
          ...params,
        });
        dispatch(setYearAccumulateList(response.data?.accumulatePaging || []));
        setPagination({
          ...pagination,
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );

  const deleteAccountCategory = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          accumulateId: values,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: "DELETE",
          actionId: currentEmployee,
        };
        await services.manageStatusYearAccumulate(modifiedValues);
        message.success(t("common.success"))

        onCancel();
        getYearAccumulateList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getYearAccumulateList]
  );

  const handleDelete = useCallback(
    (values) => {
      // const modifiedValues = { ...values };
      Modal.confirm({
        title: t('confirmation-1'),
        content: (
          <>
            <p>
              {t("dialog.confirmation.delete")/* .replace("{entity}", t("year_accumulate_list.entity")) */}

            </p>
          </>
        ),
        onOk() {
          if (values) {
            deleteAccountCategory(values)
          }
        },
        okText: t('confirm-2'),
        cancelText: t('masterData.cancel'),
      });
    },
    [currentCompany, deleteAccountCategory]
  );
  const handleOnChange = useCallback(
    (tablePagination, tableFilters, tableSorter, tableExtra) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getYearAccumulateList(modifiedParams);
    },
    [getYearAccumulateList]
  );

  const handleOnStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const values = {
              accumulateId: record.accumulateId,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: val ? "ACTIVE" : "INACTIVE",
              actionId: currentEmployee,
            };
            await services.manageStatusYearAccumulate(values);
            const modifiedYearAccumulateList = {
              // requestId: generateRandomString(),
              // companyId: currentCompany,
              pagination: {
                ...pagination,
                page: pagination.page - 1,
              },
            };
            getYearAccumulateList(modifiedYearAccumulateList);
            message.success(t("common.success"))
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [currentCompany, pagination, dispatch, getYearAccumulateList]
  );

  const createYearAccumulate = useCallback(
    async (values = {}) => {
      try {
        const modifiedValues = {
          accumulateName: values.accumulateName,
          accumulateNameEn: values.accumulateNameEn,
          incomeDeductionId: values.incomeDeductionId,
          requestId: generateRandomString(),
          companyId: currentCompany,
          actionId: currentEmployee,
        };
        await services.createYearAccumulateList(modifiedValues);
        dispatch(setIsLoading(true));
        message.success(t("common.success"))
        onCancel();
        getYearAccumulateList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getYearAccumulateList]
  );

  const updateYearAccumulate = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          accumulateId: values.accumulateId,
          accumulateName: values.accumulateName,
          accumulateNameEn: values.accumulateNameEn,
          incomeDeductionId: values.incomeDeductionId,
          requestId: generateRandomString(),
          companyId: currentCompany,
          actionId: currentEmployee,
        };
        await services.updateYearAccumulateList(modifiedValues);
        message.success(t("common.success"))

        onCancel();
        getYearAccumulateList({ pagination: { page: 0, size: 10 } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getYearAccumulateList]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = { ...values, companyId: currentCompany };
      Modal.confirm({
        title: t('confirmation-1'),
        content: (
          <>
            <p>
              {(values.id
                ? t("dialog.confirmation.edit")
                : t("dialog.confirmation.create")
              ).replace("{entity}", t("year_accumulate_list.entity"))}
            </p>
          </>
        ),
        onOk() {
          if (values.accumulateId) {
            updateYearAccumulate(modifiedValues);
          } else {
            createYearAccumulate(modifiedValues);
          }
        },
        okText: t('confirm-2'),
        cancelText: t('masterData.cancel'),
      });
    },
    [currentCompany, updateYearAccumulate, createYearAccumulate]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getYearAccumulateList({ pagination: { page: 0, size: 10 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);


  return {
    onSearch,
    onSubmit,
    onCreate,
    onEdit,
    onCancel,
    initialValues,
    isModalOpen,
    onDelete: handleDelete,
    yearAccumulateList,
    isLoading,
    pagination,
    onChange: handleOnChange,
    onStatus: handleOnStatus,
    amountIncomeDeductionOptions,
  };
}

export default useYearAccumulateList;
